import { useCallback, useEffect, useRef, useState } from 'react';

/** How long a success notice stays up. Long enough to read, short enough to forget. */
export const NOTICE_MS = 4000;

/**
 * State that falls back to its initial value on its own.
 *
 * For "Saved." style confirmations: they should register and then get out of
 * the way, rather than sit above the page until something else replaces them.
 * Setting the value again restarts the clock; setting it back to `initial`
 * clears immediately. Error banners deliberately do not use this — a problem
 * has to stay visible until the reader has dealt with it.
 */
export function useTransient<T>(initial: T, ms = NOTICE_MS): [T, (next: T) => void] {
  const [value, setValue] = useState(initial);
  const timer = useRef<number | undefined>(undefined);

  const set = useCallback(
    (next: T) => {
      window.clearTimeout(timer.current);
      setValue(next);
      if (next !== initial) {
        timer.current = window.setTimeout(() => setValue(initial), ms);
      }
    },
    [initial, ms],
  );

  // Never let a timer fire into an unmounted page.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  return [value, set];
}
